import { getApplicationURL } from "../common/utilityFunctions";

export interface GandalfProps {
  identity_provider: string;
  require_email_verification: "true" | "false";
}

export enum AuthEndpoint {
  SIGN_IN = "signIn",
  SIGN_OUT = "signOut",
  REFRESH_TOKEN = "refreshToken",
  DECODE_TOKEN = "decodeToken",
}

export const addParamAndEndpointToBuildFullAuthURL = ({
  endpoint,
  gandalfProps,
  force,
}: {
  endpoint: AuthEndpoint;
  gandalfProps?: GandalfProps;
  force?: boolean;
}): string => {
  const applicationURL = getApplicationURL();
  const urlWithPath = new URL(`${applicationURL}/${endpoint}`);
  urlWithPath.searchParams.append("redirect_uri", applicationURL);
  if (gandalfProps) {
    urlWithPath.searchParams.append("identity_provider", gandalfProps.identity_provider);
    urlWithPath.searchParams.append("require_email_verification", gandalfProps.require_email_verification);
  }
  if (force) {
    urlWithPath.searchParams.append("force", "true");
  }
  return urlWithPath.toString();
};

export const getSignInURL = (gandalfProps?: GandalfProps) => {
  return addParamAndEndpointToBuildFullAuthURL({ endpoint: AuthEndpoint.SIGN_IN, gandalfProps });
};

export const signOut = async (): Promise<Response> => {
  const signOutURL = addParamAndEndpointToBuildFullAuthURL({ endpoint: AuthEndpoint.SIGN_OUT });
  const signOutResponse = await fetch(signOutURL, {
    credentials: "same-origin",
  });
  return signOutResponse;
};

export const refreshToken = async (): Promise<Response> => {
  try {
    const refreshTokenURL = addParamAndEndpointToBuildFullAuthURL({
      endpoint: AuthEndpoint.REFRESH_TOKEN,
      force: true,
    });
    const refreshTokenResponse = await fetch(refreshTokenURL, { method: "POST" });
    return refreshTokenResponse;
  } catch (err) {
    throw Error("Error refreshing token");
  }
};

export const decodeToken = async (): Promise<Response> => {
  const decodeTokenURL = addParamAndEndpointToBuildFullAuthURL({ endpoint: AuthEndpoint.DECODE_TOKEN });
  const decodeTokenResponse = await fetch(decodeTokenURL, {
    credentials: "include",
  });
  return decodeTokenResponse;
};

export const attemptTokenRefresh = async (): Promise<{ success: boolean }> => {
  try {
    const refreshTokenResponse = await refreshToken();
    return { success: refreshTokenResponse.ok };
  } catch (error) {
    return { success: false };
  }
};

/**
 * Determines if an authentication token is expired or approaching expiration
 *
 * @param expirationTime - Token expiration timestamp in seconds
 * @param timeWindowMinutes - Time window to check before expiration
 * @returns boolean - True if token has expired or will expire within the time window, false otherwise
 */
export const isTokenExpiredOrExpiringSoon = (expirationTime: number, timeWindowMinutes: number): boolean => {
  const currentTime = Date.now();
  const timeWindowMs = timeWindowMinutes * 60 * 1000;
  const expirationTimeMs = expirationTime * 1000;
  return expirationTimeMs - currentTime <= timeWindowMs;
};
export interface TokenCheckResult {
  refreshed: boolean;
  isValid: boolean;
  expirationTime?: number;
}

/**
 * Checks token validity and refreshes if needed
 *
 * @param timeWindowMinutes - Time window (in minutes) before token expiration to trigger refresh (default: 5)
 * @returns TokenCheckResult object containing token status information
 *
 * TokenCheckResult properties:
 * - refreshed: boolean - Indicates if token needed/needs refresh
 * - isValid: boolean - Current token validity status
 */
export const checkAndRefreshTokenIfNeeded = async (timeWindowMinutes = 5): Promise<TokenCheckResult> => {
  try {
    const decodedTokenResponse = await decodeToken();

    // Handle invalid decode response
    if (!decodedTokenResponse.ok) {
      const { success } = await attemptTokenRefresh();
      return { refreshed: success, isValid: success };
    }

    const decodedToken = await decodedTokenResponse.json();

    // Handle missing expiration time
    if (!decodedToken.tokenExpiration) {
      const { success } = await attemptTokenRefresh();
      return { refreshed: success, isValid: success };
    }
    const isExpiringSoon = isTokenExpiredOrExpiringSoon(decodedToken.tokenExpiration, timeWindowMinutes);
    if (isExpiringSoon) {
      const { success } = await attemptTokenRefresh();
      return { refreshed: success, isValid: success };
    }
    // Return valid token status (not expiring soon)
    return {
      refreshed: false,
      isValid: true,
    };
  } catch (error) {
    const { success } = await attemptTokenRefresh();
    return { refreshed: success, isValid: success };
  }
};
